import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { DefaultLayout } from '@/shared/ui/DefaultLayout'
import { QueryState } from '@/shared/ui/QueryState'
import { KakaoPointMap } from '@/shared/ui/KakaoPointMap'
import { api } from '@/shared/lib/api'
import { PUBLIC_STALE_TIME_MS } from '@/shared/lib/query'
import type { OrgDetail } from '@/shared/lib/apiTypes'
import { useCardLabels } from '@/shared/lib/cardLabels'
import { makeLabeler, makeRegionLabeler, useCommonCodes, useRegions } from '@/shared/lib/codes'
import { CARD_CATEGORY_MAX } from '@/shared/constants/cards'
import { SupportInfoPanel } from '@/public/features/solution/ui/SupportInfoPanel'
import { CityIcon } from '@/shared/ui/icons/CityIcon'
import { InfoIcon } from '@/shared/ui/icons/InfoIcon'
import { LocationPinIcon } from '@/shared/ui/icons/LocationPinIcon'
import { ChevronRightIcon } from '@/shared/ui/icons/ChevronRightIcon'

const SIDE_CARD_CHIP_MAX = 2

const SIDE_CARD_MAX = 2

export function OrgDetailPc({ query }: { query: UseQueryResult<OrgDetail> }) {
  const { categoryLabel, targetLabel, isReady } = useCardLabels()
  const resourceLabel = makeLabeler(useCommonCodes('resource').data)
  const areaLabel = makeLabeler(useCommonCodes('service_area').data)
  const costLabel = makeLabeler(useCommonCodes('cost_type').data)
  const orgTypeLabel = makeLabeler(useCommonCodes('org_type').data)
  const regionLabel = makeRegionLabeler(useRegions().data)

  const [params] = useSearchParams()
  const fromId = params.get('from')
  const fromProblem = useQuery({
    queryKey: ['problem-card', fromId],
    queryFn: () => api.problemCard(fromId ?? ''),
    staleTime: PUBLIC_STALE_TIME_MS,
    enabled: fromId !== null,
    retry: false,
  })

  const o = query.data

  return (
    <DefaultLayout tree="pc" className="[--body-w:1000px]">
      <QueryState isPending={query.isPending || !isReady} error={query.error}>
        {o === undefined ? null : (
          <div className="layout-body pt-8 pb-[60px]">
            <nav className="flex items-center gap-2 text-body-sm font-medium text-black-100">
              {fromProblem.data === undefined ? (
                <Link className="text-inherit no-underline" to="/solutions">
                  기업 해결 카드
                </Link>
              ) : (
                <>
                  <Link className="text-inherit no-underline" to="/problems">
                    시민문제
                  </Link>
                  <ChevronRightIcon size={16} />
                  <Link
                    className="text-inherit no-underline"
                    to={`/problems/${fromProblem.data.id}`}
                  >
                    {fromProblem.data.title}
                  </Link>
                </>
              )}
              <ChevronRightIcon size={16} />
              <span>기업 자세히 보기</span>
            </nav>

            <div className="mt-[46px] flex items-start gap-11">
              <div className="flex w-[640px] shrink-0 flex-col gap-6">
                <div className="flex items-start gap-[18px]">
                  <span className="flex h-[100px] w-[100px] shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-violet-100 text-black-100 [&>svg]:opacity-50">
                    {o.logoUrl !== null && o.logoUrl !== undefined ? (
                      <img className="h-full w-full object-cover" src={o.logoUrl} alt="" />
                    ) : (
                      <CityIcon size={40} />
                    )}
                  </span>
                  <div className="flex flex-col gap-[14px]">
                    <span className="inline-flex w-fit items-center justify-center rounded-lg bg-white-200 px-3 py-[3px] text-body-sm font-medium whitespace-nowrap text-black-600">
                      {orgTypeLabel(o.orgType)}
                    </span>
                    <div className="flex flex-col gap-[14px]">
                      <p className="m-0 text-headline-xs font-semibold text-black-800">{o.name}</p>
                      {o.baseRegionCode !== null && (
                        <p className="m-0 flex items-center gap-1 text-body-md text-black-300">
                          <span className="text-white-600">
                            <LocationPinIcon size={20} />
                          </span>
                          {regionLabel(o.baseRegionCode)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  <LabeledText label="기업·기관 소개" text={o.intro} />
                  <LabeledText label="기업 미션" text={o.mission} />
                  <LabeledText label="기업 비전" text={o.vision} />

                  {o.homepageUrl !== null && o.homepageUrl !== undefined && (
                    <a
                      className="text-body-sm-reading font-medium break-all text-violet-700"
                      href={o.homepageUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {o.homepageUrl}
                    </a>
                  )}

                  {o.location !== null && (
                    <section className="flex flex-col gap-[19px]">
                      <h2 className="m-0 text-caption font-medium text-black-100">위치</h2>
                      <div className="flex flex-col gap-[6px]">
                        <KakaoPointMap
                          lat={o.location.lat}
                          lng={o.location.lng}
                          className="h-[180px] w-full overflow-hidden rounded-[14px] border border-white-200 bg-white-50 [&_.map-canvas]:h-full [&_.map-canvas]:rounded-[inherit] [&_.map-canvas]:border-0"
                        />
                        {o.baseRegionCode !== null && (
                          <p className="m-0 flex items-start gap-1 text-body-sm-reading text-black-500">
                            <span className="text-white-600">
                              <LocationPinIcon size={20} />
                            </span>
                            {regionLabel(o.baseRegionCode)}
                          </p>
                        )}
                      </div>
                    </section>
                  )}
                </div>
              </div>

              <aside className="flex w-[316px] shrink-0 flex-col gap-[18px] self-stretch overflow-clip rounded-[18px] bg-[#f8f9fc] px-[26px] py-7">
                <div className="flex items-center gap-2">
                  <h2 className="m-0 text-body-lg font-semibold text-black-600">기업의 해결 카드</h2>
                  <span className="inline-flex items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-yellow-50 px-3 py-[3px] text-body-sm font-medium whitespace-nowrap text-yellow-800">
                    총 {o.solutionCards.length}장
                  </span>
                </div>
                <ul className="m-0 flex list-none flex-col gap-5 p-0">
                  {o.solutionCards.slice(0, SIDE_CARD_MAX).map((card) => (
                    <li key={card.id}>
                      <Link
                        className="flex flex-col gap-[18px] rounded-2xl border border-white-200 bg-white-50 px-6 py-[22px] text-inherit no-underline shadow-[0_0_4px_-1px_rgb(0_0_0/8%),0_0_12px_-4px_rgb(0_0_0/16%)]"
                        to={`/solutions/${card.id}`}
                      >
                        <span className="flex flex-col gap-[14px]">
                          <span className="flex flex-wrap items-center gap-2">
                            {card.categoryCodes.slice(0, SIDE_CARD_CHIP_MAX).map((code) => (
                              <span
                                key={code}
                                className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-violet-800 px-3 py-1 text-body-sm font-medium whitespace-nowrap text-white-50"
                              >
                                {categoryLabel(code)}
                              </span>
                            ))}
                            {card.categoryCodes.length > SIDE_CARD_CHIP_MAX && (
                              <span className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-white-200 px-3 py-1 text-body-sm font-medium whitespace-nowrap text-black-500">
                                +{card.categoryCodes.length - SIDE_CARD_CHIP_MAX}
                              </span>
                            )}
                          </span>
                          <span className="text-body-lg-reading font-semibold text-black-800">
                            {card.title}
                          </span>
                        </span>
                        {card.accessCondition != null && card.accessCondition.length > 0 && (
                          <span className="flex items-center gap-1 text-body-sm font-normal text-violet-600">
                            <InfoIcon size={20} />
                            {card.accessCondition}
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </aside>
            </div>

            {o.solutionCards.length > 0 && (
              <section className="mt-[60px]">
                <h2 className="m-0 flex h-8 items-center text-body-lg font-semibold text-black-600">
                  기업 해결카드 상세정보
                </h2>
                <ul className="m-0 mt-[18px] flex list-none flex-col gap-[18px] p-0">
                  {o.solutionCards.map((card) => (
                    <li key={card.id} className="flex items-start gap-4">
                      <Link
                        className="flex w-[320px] shrink-0 flex-col gap-[18px] rounded-2xl border border-white-200 bg-white-50 px-6 py-[22px] text-inherit no-underline shadow-[0_0_4px_-1px_rgb(0_0_0/8%),0_0_12px_-4px_rgb(0_0_0/16%)]"
                        to={`/solutions/${card.id}`}
                      >
                        <span className="flex flex-col gap-[12px]">
                          <span className="text-body-lg-reading font-semibold text-black-800">
                            {card.title}
                          </span>
                          <span className="line-clamp-3 text-body-md-reading font-normal text-black-800">
                            {card.summary}
                          </span>
                        </span>
                        {card.accessCondition != null && card.accessCondition.length > 0 && (
                          <span className="flex items-center gap-1 text-body-sm font-normal text-violet-600">
                            <InfoIcon size={20} />
                            {card.accessCondition}
                          </span>
                        )}
                        <span className="flex flex-wrap items-center gap-2">
                          {card.categoryCodes.slice(0, CARD_CATEGORY_MAX).map((code) => (
                            <span
                              key={code}
                              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-[rgb(0_0_0/5%)] bg-violet-800 px-3 py-1 text-body-sm font-medium whitespace-nowrap text-white-50"
                            >
                              {categoryLabel(code)}
                            </span>
                          ))}
                        </span>
                      </Link>
                      <SupportInfoPanel
                        className="min-w-px flex-1 rounded-[24px] bg-[#f8f9fc] px-6 py-[18px]"
                        resourceChips={(card.resourceCodes ?? []).map(resourceLabel)}
                        targetChips={(card.targetCodes ?? []).map(targetLabel)}
                        region={
                          card.serviceDongCodes != null && card.serviceDongCodes.length > 0
                            ? card.serviceDongCodes.map(regionLabel).join(' · ')
                            : card.serviceAreaCodes.map(areaLabel).join(' · ')
                        }
                        cost={card.costType == null ? '' : costLabel(card.costType)}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </QueryState>
    </DefaultLayout>
  )
}

function LabeledText({ label, text }: { label: string; text: string | null }) {
  if (text === null || text.length === 0) return null
  return (
    <section className="flex flex-col gap-[6px]">
      <h2 className="m-0 text-caption font-medium text-black-100">{label}</h2>
      <p className="m-0 text-body-md-reading whitespace-pre-wrap text-black-800">{text}</p>
    </section>
  )
}
